import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { hasFirebaseConfig, getFirebase } from '../firebase';
import { useProfile } from '../hooks/useProfile';

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(hasFirebaseConfig);
  const [error, setError] = useState(null);
  const { profile, profileLoading, isOnboarded, loadProfile, saveProfile, clearProfile } = useProfile();

  // Listen for Firebase auth state if configured
  useEffect(() => {
    if (!hasFirebaseConfig) return;

    let unsub = () => {};
    getFirebase().then(fb => {
      if (!fb) { setLoading(false); return; }
      unsub = fb.onAuthStateChanged(fb.auth, async (firebaseUser) => {
        if (firebaseUser) {
          const userData = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            provider: firebaseUser.providerData?.[0]?.providerId || 'unknown',
          };
          setUser(userData);
          // Load profile from Firestore on sign-in
          await loadProfile(firebaseUser.uid);
        } else {
          setUser(null);
        }
        setLoading(false);
      });
    }).catch(() => setLoading(false));

    return () => unsub();
  }, [loadProfile]);

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    const fb = await getFirebase();
    if (!fb) { setError('Google sign-in requires Firebase setup. Add your Firebase credentials to frontend/.env — use email for now.'); return null; }
    try {
      const result = await fb.signInWithPopup(fb.auth, fb.googleProvider);
      return result.user;
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user') return null;
      setError(err.message);
      throw err;
    }
  }, []);

  const signInWithApple = useCallback(async () => {
    setError(null);
    const fb = await getFirebase();
    if (!fb) { setError('Apple sign-in requires Firebase setup. Add your Firebase credentials to frontend/.env — use email for now.'); return null; }
    try {
      const result = await fb.signInWithPopup(fb.auth, fb.appleProvider);
      return result.user;
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user') return null;
      setError(err.message);
      throw err;
    }
  }, []);

  const sendEmailLink = useCallback(async (email) => {
    setError(null);
    const fb = await getFirebase();
    if (!fb) { setError('Email sign-in requires Firebase setup.'); return false; }
    try {
      const actionCodeSettings = {
        url: window.location.origin + '/onboarding',
        handleCodeInApp: true,
      };
      await fb.sendSignInLinkToEmail(fb.auth, email, actionCodeSettings);
      try { localStorage.setItem('emailForSignIn', email); } catch (e) {}
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, []);

  // Handle email link sign-in on page load
  useEffect(() => {
    if (!hasFirebaseConfig) return;
    getFirebase().then(fb => {
      if (!fb || !fb.isSignInWithEmailLink(fb.auth, window.location.href)) return;
      let email;
      try { email = localStorage.getItem('emailForSignIn'); } catch (e) {}
      if (!email) {
        email = window.prompt('Please enter your email to confirm sign-in');
      }
      if (!email) return;
      fb.signInWithEmailLink(fb.auth, email, window.location.href)
        .then(() => {
          try { localStorage.removeItem('emailForSignIn'); } catch (e) {}
          window.history.replaceState(null, '', window.location.pathname);
        })
        .catch(err => setError(err.message));
    });
  }, []);

  const sendPhoneCode = useCallback(async (phoneNumber, recaptchaContainerId) => {
    setError(null);
    const fb = await getFirebase();
    if (!fb) { setError('Phone sign-in requires Firebase setup.'); return null; }
    try {
      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new fb.RecaptchaVerifier(fb.auth, recaptchaContainerId, { size: 'invisible' });
      }
      const confirmation = await fb.signInWithPhoneNumber(fb.auth, phoneNumber, window.recaptchaVerifier);
      return confirmation;
    } catch (err) {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
      setError(err.message);
      throw err;
    }
  }, []);

  const verifyPhoneCode = useCallback(async (confirmationResult, code) => {
    setError(null);
    try {
      const result = await confirmationResult.confirm(code);
      return result.user;
    } catch (err) {
      setError('Invalid verification code. Please try again.');
      throw err;
    }
  }, []);

  const signOutUser = useCallback(async () => {
    try {
      const fb = await getFirebase();
      if (fb) await fb.signOut(fb.auth);
      setUser(null);
      clearProfile();
    } catch (err) {
      setError(err.message);
    }
  }, [clearProfile]);

  const clearError = useCallback(() => setError(null), []);

  const hasEmail = (() => {
    try { return !!localStorage.getItem('touchstone_email'); }
    catch { return false; }
  })();

  const isDev = import.meta.env.DEV;
  const devUser = isDev ? { uid: 'dev-local', email: 'dev@localhost', displayName: 'Dev User' } : null;

  const value = {
    user: user || devUser,
    loading,
    error,
    isSignedIn: isDev || !!user || hasEmail,
    hasFirebaseConfig,
    // Auth methods
    signInWithGoogle,
    signInWithApple,
    sendEmailLink,
    sendPhoneCode,
    verifyPhoneCode,
    signOut: signOutUser,
    clearError,
    // Profile (from Firestore + localStorage)
    profile,
    profileLoading,
    isOnboarded,
    saveProfile: (newProfile) => saveProfile(user?.uid || devUser?.uid, newProfile),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
